import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, EventEmitter, Output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { PlatformService } from '@services/platforms/platform.service';
import { ProductService } from '@services/products/product.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { SharedModule } from '@shared/modules/shared.module';

interface OrderItem {
	id: string;
	name: string;
}

/**
 * Dialog to set the display order of a platform's products (the order shown in the service selector).
 */
@Component({
	selector: 'app-product-order',
	standalone: true,
	imports: [SharedModule, ButtonComponent, SelectComponent, CdkDropList, CdkDrag, CdkDragHandle],
	templateUrl: './product-order.component.html',
	styleUrl: './product-order.component.scss',
})
export class ProductOrderComponent {
	@Output() orderSaved = new EventEmitter<void>();

	visible = false;
	isLoading = false;
	isSaving = false;
	platformId: string | null = null;
	platformOptions: { label: string; value: string }[] = [];
	items: OrderItem[] = [];

	constructor(
		private productService: ProductService,
		private platformService: PlatformService,
		private messageAlertService: MessageAlertService,
		private translocoService: TranslocoService,
		private cdr: ChangeDetectorRef,
	) {}

	show(platformId?: string): void {
		// Skeleton from the first frame: the sheet opens at its final size instead of growing
		// (and jumping) as the platforms and then the products arrive
		this.items = [];
		this.isLoading = true;
		this.visible = true;
		this.platformService.getAllPlatforms().subscribe({
			next: (platforms) => {
				this.platformOptions = [...platforms]
					.sort((a: any, b: any) => Number(a.id) - Number(b.id))
					.map((p: any) => ({ label: p.name, value: p.id.toString() }));
				this.onPlatformChange(platformId || this.platformOptions[0]?.value || null);
			},
			error: (err) => console.error('Error loading platforms:', err),
		});
	}

	onPlatformChange(platformId: string | null): void {
		this.platformId = platformId;
		this.items = [];
		if (!platformId) return;

		this.isLoading = true;
		this.cdr.detectChanges();
		this.productService
			.getProductsByPlatform(platformId, { sort_by: 'displayOrder', sort_order: 'asc' })
			.subscribe({
				next: (response) => {
					this.items = response.data.map((p) => ({ id: p.id.toString(), name: p.name }));
					this.isLoading = false;
					this.cdr.detectChanges();
				},
				error: (err) => {
					console.error('Error loading products:', err);
					this.isLoading = false;
					this.cdr.detectChanges();
				},
			});
	}

	drop(event: CdkDragDrop<OrderItem[]>): void {
		moveItemInArray(this.items, event.previousIndex, event.currentIndex);
	}

	save(): void {
		if (!this.platformId || this.items.length === 0) return;

		this.isSaving = true;
		this.productService
			.reorderProducts(
				this.platformId,
				this.items.map((item) => item.id),
			)
			.subscribe({
				next: () => {
					this.isSaving = false;
					this.visible = false;
					this.messageAlertService.showMessage(
						'success',
						this.translocoService.translate('productsCrud.order.saved'),
						'',
					);
					this.orderSaved.emit();
				},
				error: () => {
					this.isSaving = false;
					this.messageAlertService.showMessage(
						'error',
						this.translocoService.translate('common.labels.error'),
						this.translocoService.translate('productsCrud.order.error'),
					);
					this.cdr.detectChanges();
				},
			});
	}
}
